import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { SideNavigation, SideNavigationProps, Flashbar, TopNavigation } from '@cloudscape-design/components';
import { HelpProvider, useHelpPanel } from '../contexts/HelpContext';
import Dashboard from '../pages/Dashboard';
import EventTesting from '../pages/EventTesting';
import EmailPreview from '../pages/EmailPreview';
import NotificationMonitoring from '../pages/NotificationMonitoring';
import Configuration from '../pages/Configuration';
import Login from '../pages/Login';
import Callback from '../pages/Callback';
import Unauthorized from '../pages/Unauthorized';
import { useNotifications } from '../hooks/useNotifications';
import { useAuth } from '../contexts/AuthContext';
import { CustomAppLayout } from './CustomAppLayout';
import ProtectedRoute from './ProtectedRoute';
import { DashboardHelp } from './help/DashboardHelp';
import { EventTestingHelp } from './help/EventTestingHelp';
import { EmailPreviewHelp } from './help/EmailPreviewHelp';
import { NotificationMonitoringHelp } from './help/NotificationMonitoringHelp';
import { ConfigurationHelp } from './help/ConfigurationHelp';

// Helper function to get a meaningful display name for the user
const getUserDisplayName = (user: any): string => {
  if (!user) return 'Guest';
  
  // For Midway users, try to get a meaningful name
  if (user.attributes?.given_name && user.attributes?.family_name) {
    return `${user.attributes.given_name} ${user.attributes.family_name}`;
  }
  
  if (user.attributes?.email) {
    return user.attributes.email;
  }
  
  // Check for Midway identity
  if (user.signInUserSession?.idToken?.payload?.identities?.[0]) {
    const identity = user.signInUserSession.idToken.payload.identities[0];
    if (identity.providerName === 'FederateIdentityProvider') {
      return identity.userId || 'Midway User';
    }
  }
  
  return 'User';
};

// Wrapper component that has access to the help context
function AppContentWithHelp() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeHref, setActiveHref] = useState<string>(location.pathname);
  const { notifications, addNotification, removeNotification } = useNotifications();
  const { isAuthenticated, isAdmin, user, signOut } = useAuth();
  const { content, isToolsOpen, setIsToolsOpen, setContent, setContentOnly } = useHelpPanel();
  
  // Map routes to help components
  const routeHelpMap: Record<string, React.ReactNode> = {
    '/': <DashboardHelp />,
    '/event-testing': <EventTestingHelp />,
    '/email-preview': <EmailPreviewHelp />,
    '/notifications': <NotificationMonitoringHelp />,
    '/configuration': <ConfigurationHelp />
  };
  
  // Set initial content when component mounts
  useEffect(() => {
    const helpContent = routeHelpMap[activeHref];
    if (helpContent) {
      setContentOnly(helpContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this runs once on mount
  
  // Set help content when route changes without affecting panel state
  useEffect(() => {
    const helpContent = routeHelpMap[activeHref];
    if (helpContent) {
      setContentOnly(helpContent);
    }
  }, [activeHref, setContentOnly, routeHelpMap]);

  // Update activeHref whenever location changes
  useEffect(() => {
    setActiveHref(location.pathname);
  }, [location.pathname]);

  // Side navigation items - only show if authenticated
  const navItems: SideNavigationProps.Item[] = [
    { type: 'link' as const, text: 'Dashboard', href: '/' },
    { type: 'link' as const, text: 'Event Testing', href: '/event-testing' },
    { type: 'link' as const, text: 'Email Preview', href: '/email-preview' },
    { type: 'link' as const, text: 'Notification Monitoring', href: '/notifications' },
    { type: 'divider' as const },
    // Only show Configuration to admins
    ...(isAdmin ? [{ type: 'link' as const, text: 'Configuration', href: '/configuration' }] : []),
  ];

  const handleNavigate = (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
    if (event.detail.href) {
      setActiveHref(event.detail.href);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      addNotification({
        type: 'error',
        content: 'Failed to sign out. Please try again.',
        dismissible: true,
        onDismiss: () => removeNotification('sign-out-error')
      });
    }
  };

  return (
    <CustomAppLayout
      contentType="dashboard"
      tools={content}
      toolsOpen={isToolsOpen}
      onToolsChange={({ detail }) => setIsToolsOpen(detail.open)}
      navigation={
        isAuthenticated ? (
          <SideNavigation 
            header={{ text: 'Canary Navigation', href: '/' }} 
            items={navItems}
            activeHref={activeHref}
            onFollow={handleNavigate}
          />
        ) : null
      }
      notifications={<div>{notifications && <Flashbar items={notifications} />}</div>}
      breadcrumbs={undefined}
      content={
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/callback" element={<Callback />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <Dashboard addNotification={addNotification} />
            </ProtectedRoute>
          } />
          <Route path="/event-testing" element={
            <ProtectedRoute>
              <EventTesting addNotification={addNotification} />
            </ProtectedRoute>
          } />
          <Route path="/email-preview" element={
            <ProtectedRoute>
              <EmailPreview addNotification={addNotification} />
            </ProtectedRoute>
          } />
          <Route path="/notifications" element={
            <ProtectedRoute>
              <NotificationMonitoring addNotification={addNotification} />
            </ProtectedRoute>
          } />
          
          {/* Admin-only routes */}
          <Route path="/configuration" element={
            <ProtectedRoute requireAdmin={true}>
              <Configuration addNotification={addNotification} />
            </ProtectedRoute>
          } />
          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />} />
        </Routes>
      }
      header={
        <TopNavigation
          identity={{
            href: '/',
            title: 'Canary | AWS Solutions Notification System',
            logo: {
              src: './logo.png',
              alt: 'AWS Logo'
            }
          }}
          utilities={[
            ...(isAuthenticated ? [
              {
                type: 'menu-dropdown' as const,
                text: getUserDisplayName(user),
                description: isAdmin ? 'Administrator' : 'Standard user',
                items: [
                  { id: 'profile', text: 'Profile settings' },
                  { id: 'divider', text: '', disabled: true },
                  { 
                    id: 'signout', 
                    text: 'Sign out'
                  },
                ],
                onItemClick: (event: { detail: { id: string } }) => {
                  if (event.detail.id === 'signout') {
                    handleSignOut();
                  }
                }
              }
            ] : []),
            {
              type: 'menu-dropdown' as const,
              text: 'Settings',
              title: 'Settings',
              items: [
                { id: 'theme-light', text: 'Light mode' },
                { id: 'theme-dark', text: 'Dark mode' },
              ]
            },
            {
              type: 'menu-dropdown' as const,
              text: 'Help',
              title: 'Help',
              items: [
                { id: 'documentation', text: 'Documentation', href: '#' },
                { id: 'support', text: 'Support', href: '#' },
              ]
            }
          ]}
        />
      }
    />
  );
}

// Main component that provides the help context
export function AppContent() {
  return (
    <HelpProvider>
      <AppContentWithHelp />
    </HelpProvider>
  );
}
